// PurchaseOrderEntryPage.js - Complete with Global Components
// VERSION: 3.1 - Using Global Components including StatusBadge, TrackingModal
// Fixes: NF-e Modal visibility - nested modal rendering issue resolved

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import useAuth from '../../hooks/useAuth';
import usePagePermissions from '../../hooks/usePagePermissions';
import { db } from '../../firebaseConfig';
import { addDoc, collection, serverTimestamp, runTransaction, getDocs, getDoc, doc, query, where } from 'firebase/firestore';
import ConfirmDialog from '../../components/common/ConfirmDialog/ConfirmDialog';
import Toast from '../../components/common/Toast/Toast';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import Pagination from '../../components/common/Pagination/Pagination';
import TableSkeleton from '../../components/common/TableSkeleton/TableSkeleton';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import TrackingModal from '../../components/common/TrackingModal/TrackingModal';
import styles from './Orders.module.css';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiFileText,
    FiSearch,
    FiPackage,
    FiClipboard,
    FiCalendar,
    FiTag,
    FiMapPin,
    FiList,
    FiEye,
    FiPlus,
    FiUpload,
    FiPrinter,
    FiChevronUp,
    FiChevronDown,
    FiClock,
    FiUser,
    FiBox,
    FiDroplet,
    FiFolder,
    FiHome,
    FiBriefcase,
    FiTruck,
    FiShoppingCart,
    FiDollarSign,
    FiPercent,
    FiCreditCard,
    FiSend,
    FiFlag,
    FiCheckCircle,
    FiXCircle,
    FiClock as FiClockIcon,
    FiTrendingUp,
    FiUsers,
    FiPhone,
    FiMail,
    FiMap,
    FiNavigation,
    FiCalendar as FiCalendarIcon,
    FiMessageSquare,
    FiSettings,
    FiStar,
    FiHeart,
    FiThumbsUp,
    FiThumbsDown,
    FiMoreHorizontal,
    FiAward,
    FiDollarSign as FiMoney,
    FiArrowLeft as FiArrowLeftIcon,
    FiArrowRight as FiArrowRightIcon,
    FiPlusCircle,
    FiMinusCircle,
    FiInbox,
    FiExternalLink
} from 'react-icons/fi';
import {
    MdEdit,
    MdDelete,
    MdAdd,
    MdSave,
    MdCancel,
    MdVisibility,
    MdPrint,
    MdLocationOn,
    MdBusiness,
    MdPerson,
    MdPhone,
    MdEmail,
    MdAttachMoney,
    MdReceipt,
    MdLocalShipping,
    MdCheckCircle,
    MdCancel as MdCancelIcon,
    MdPending,
    MdApproval,
    MdProductionQuantityLimits,
    MdDeliveryDining,
    MdFactCheck,
    MdMoney,
    MdMonetizationOn,
    MdOutlineCheckCircle,
    MdOutlineCancel,
    MdOutlinePending,
    MdInventory
} from 'react-icons/md';
import { FaBox, FaTruck, FaUser, FaBuilding, FaMoneyBillWave, FaClipboardList, FaDollarSign, FaPrint, FaCheck, FaTimes, FaEye, FaMapMarkerAlt, FaBoxOpen, FaWarehouse } from 'react-icons/fa';

// ============================================
// 1. CONSTANTES E DEFINIÇÕES
// ============================================
const PURCHASE_ORDERS_COLLECTION = 'purchase-orders';
const RECEIVING_RECORDS_COLLECTION = 'receiving-records';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const STOCK_MOVEMENTS_COLLECTION = 'stock-movements';
const ITEMS_PER_PAGE = 10;

// Status do Pedido
const ORDER_STATUS = {
    PENDING: 'pending',
    APPROVED: 'approved',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
    REJECTED: 'rejected',
    PENDING_ENTRY: 'pending_entry',
    PARTIALLY_RECEIVED: 'partially_received',
    FULLY_RECEIVED: 'fully_received'
};

const ORDER_STATUS_LABELS = {
    [ORDER_STATUS.PENDING]: { label: 'Pendente', className: 'statusBadgePending' },
    [ORDER_STATUS.APPROVED]: { label: 'Aprovado', className: 'statusBadgeApproved' },
    [ORDER_STATUS.COMPLETED]: { label: 'Concluído', className: 'statusBadgeCompleted' },
    [ORDER_STATUS.CANCELLED]: { label: 'Cancelado', className: 'statusBadgeCancelled' },
    [ORDER_STATUS.REJECTED]: { label: 'Rejeitado', className: 'statusBadgeRejected' },
    [ORDER_STATUS.PENDING_ENTRY]: { label: 'Aguardando Entrada', className: 'statusBadgeInfo' },
    [ORDER_STATUS.PARTIALLY_RECEIVED]: { label: 'Recebimento Parcial', className: 'statusBadgeWarning' },
    [ORDER_STATUS.FULLY_RECEIVED]: { label: 'Totalmente Recebido', className: 'statusBadgeSuccess' }
};

// Delivery Types mapping
const DELIVERY_TYPES = [
    { value: 'transportadora', label: 'Na transportadora' },
    { value: 'retirar', label: 'À retirar' },
    { value: 'nosso_endereco', label: 'Nosso endereço' },
    { value: 'triangular', label: 'Triangular' }
];

const FREIGHT_BY_OPTIONS = [
    { value: 'emitente', label: 'Emitente' },
    { value: 'destinatario', label: 'Destinatário' }
];

// Helper functions
const getStatusClassStatic = (status) => {
    const statusConfig = ORDER_STATUS_LABELS[status] || ORDER_STATUS_LABELS[ORDER_STATUS.PENDING];
    return statusConfig.className;
};

const getStatusLabelStatic = (status) => {
    const statusConfig = ORDER_STATUS_LABELS[status] || ORDER_STATUS_LABELS[ORDER_STATUS.PENDING];
    return statusConfig.label;
};

// ============================================
// 2. MANUAL STOCK ENTRY MODAL (FIXED)
// ============================================
const ManualStockEntryModal = ({ onClose, onConfirm, preselectedSupplierId, preselectedSupplierName }) => {
    const [movementType, setMovementType] = useState('entrada');
    const [selectedMaterialId, setSelectedMaterialId] = useState('');
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [quantity, setQuantity] = useState('');
    const [unitCost, setUnitCost] = useState('');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [manufacturingDate, setManufacturingDate] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [receiptObservations, setReceiptObservations] = useState('');
    const [inventory, setInventory] = useState('B2E');
    const [searchProductTerm, setSearchProductTerm] = useState('');
    const [showProductSearch, setShowProductSearch] = useState(false);
    const [selectedSupplierId, setSelectedSupplierId] = useState(preselectedSupplierId || '');
    const [selectedSupplierName, setSelectedSupplierName] = useState(preselectedSupplierName || '');
    const [formLoading, setFormLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    const { currentUser } = useAuth();

    // Get raw materials from Firestore
    const { data: rawMaterials, loading: materialsLoading } = useFirestore(RAW_MATERIALS_COLLECTION, 'code');
    const { data: suppliers, loading: suppliersLoading } = useFirestore('suppliers', 'razaoSocial');

    // Set default dates
    useEffect(() => {
        const today = new Date();
        const formattedToday = today.toISOString().split('T')[0];
        setManufacturingDate(formattedToday);

        const twoYearsFromNow = new Date();
        twoYearsFromNow.setFullYear(today.getFullYear() + 2);
        setExpiryDate(twoYearsFromNow.toISOString().split('T')[0]);
    }, []);

    // Get available suppliers for the selected material
    const availableSuppliersForMaterial = useMemo(() => {
        if (!selectedMaterial || !suppliers) return [];

        const materialSuppliers = selectedMaterial.suppliers || [];
        return materialSuppliers
            .map(supplierInfo => {
                const supplier = suppliers.find(s => s.id === supplierInfo.supplierId);
                return supplier ? {
                    id: supplier.id,
                    name: supplier.razaoSocial,
                    valor: supplierInfo.valor
                } : null;
            })
            .filter(s => s !== null);
    }, [selectedMaterial, suppliers]);

    // Filter materials by search term only (no supplier filter)
    const availableMaterials = useMemo(() => {
        if (!rawMaterials) return [];

        let filtered = rawMaterials;

        if (searchProductTerm) {
            const term = searchProductTerm.toLowerCase();
            filtered = filtered.filter(material =>
                material.code?.toLowerCase().includes(term) ||
                material.commercialName?.toLowerCase().includes(term) ||
                material.inciName?.toLowerCase().includes(term)
            );
        }

        return filtered;
    }, [rawMaterials, searchProductTerm]);

    // When material is selected, update supplier options and unit cost
    useEffect(() => {
        if (selectedMaterialId && rawMaterials) {
            const material = rawMaterials.find(m => m.id === selectedMaterialId);
            if (material) {
                setSelectedMaterial(material);

                setSelectedSupplierId('');
                setSelectedSupplierName('');
                setUnitCost('');

                const materialSuppliers = material.suppliers || [];
                if (materialSuppliers.length === 1) {
                    const firstSupplier = materialSuppliers[0];
                    const supplier = suppliers?.find(s => s.id === firstSupplier.supplierId);
                    if (supplier) {
                        setSelectedSupplierId(firstSupplier.supplierId);
                        setSelectedSupplierName(supplier.razaoSocial);
                        if (firstSupplier.valor) {
                            setUnitCost(firstSupplier.valor.toString());
                        } else if (material.custoMedio) {
                            setUnitCost(material.custoMedio.toString());
                        }
                    }
                } else if (materialSuppliers.length > 1) {
                    if (material.custoMedio) {
                        setUnitCost(material.custoMedio.toString());
                    }
                } else if (material.custoMedio) {
                    setUnitCost(material.custoMedio.toString());
                }
            }
        }
    }, [selectedMaterialId, rawMaterials, suppliers]);

    // Update unit cost when supplier changes
    useEffect(() => {
        if (selectedMaterial && selectedSupplierId) {
            const supplierInfo = selectedMaterial.suppliers?.find(s => s.supplierId === selectedSupplierId);
            if (supplierInfo?.valor) {
                setUnitCost(supplierInfo.valor.toString());
            } else if (selectedMaterial.custoMedio) {
                setUnitCost(selectedMaterial.custoMedio.toString());
            }
        }
    }, [selectedSupplierId, selectedMaterial]);

    const validateForm = () => {
        const errors = {};

        if (!selectedSupplierId) {
            errors.supplier = 'Selecione um fornecedor';
        }
        if (!selectedMaterialId) {
            errors.material = 'Selecione uma matéria-prima';
        }
        if (!quantity || parseFloat(quantity) <= 0) {
            errors.quantity = 'Informe uma quantidade válida';
        }
        if (movementType === 'entrada' && !invoiceNumber) {
            errors.invoiceNumber = 'Informe o número da nota fiscal';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleConfirm = async () => {
        if (!validateForm()) return;

        setFormLoading(true);

        const responsibleName = currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário';

        const movementData = {
            type: movementType,
            movementTypeCode: movementType === 'entrada' ? 'ENTRADA_ESTOQUE' : 'SAIDA_ESTOQUE',
            movementTypeLabel: movementType === 'entrada' ? 'Entrada em Estoque' : 'Saída de Estoque',
            materialId: selectedMaterialId,
            materialCode: selectedMaterial?.code,
            materialName: selectedMaterial?.commercialName,
            materialInciName: selectedMaterial?.inciName,
            supplierId: selectedSupplierId,
            supplierName: selectedSupplierName,
            quantity: parseFloat(quantity),
            unitCost: parseFloat(unitCost) || 0,
            totalCost: (parseFloat(quantity) || 0) * (parseFloat(unitCost) || 0),
            invoiceNumber: movementType === 'entrada' ? invoiceNumber : null,
            manufacturingDate: manufacturingDate,
            expiryDate: expiryDate,
            observations: receiptObservations,
            inventory: inventory,
            responsible: responsibleName,
            responsibleId: currentUser?.id,
            status: 'completed',
            createdAt: new Date(),
            updatedAt: new Date()
        };

        await onConfirm(movementData);
        setFormLoading(false);
    };

    // Format date for input
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        return dateString.split('T')[0];
    };

    // Supplier options for the selected material
    const supplierOptionsForMaterial = useMemo(() => {
        if (!availableSuppliersForMaterial.length) return [];
        return availableSuppliersForMaterial.map(s => ({ value: s.id, label: s.name }));
    }, [availableSuppliersForMaterial]);

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={`${styles.formContainer} ${styles.formContainerLarge}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2><FiPackage size={24} style={{ marginRight: '10px' }} /> Movimentação de Estoque</h2>
                        <p className={styles.modalSubtitle}>
                            Registrar entrada ou saída manual de produtos
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.modalCloseButton}
                        disabled={formLoading}
                    >
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.formContent}>
                    {/* Tipo de Movimentação */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiRefreshCw size={18} /></div>
                            <h3 className={styles.sectionTitle}>Tipo de Movimentação</h3>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="radio"
                                        name="movementType"
                                        value="entrada"
                                        checked={movementType === 'entrada'}
                                        onChange={(e) => setMovementType(e.target.value)}
                                        disabled={formLoading}
                                    />
                                    <span>📥 Entrada em Estoque</span>
                                </label>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="radio"
                                        name="movementType"
                                        value="saida"
                                        checked={movementType === 'saida'}
                                        onChange={(e) => setMovementType(e.target.value)}
                                        disabled={formLoading}
                                    />
                                    <span>📤 Saída de Estoque</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Matéria-Prima / Produto - Now first */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                            <h3 className={styles.sectionTitle}>Matéria-Prima / Produto</h3>
                            <span className={styles.sectionBadge}>Obrigatório</span>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div className={styles.inputGroup}>
                                <label>Pesquisar Produtos</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="text"
                                        value={searchProductTerm}
                                        onChange={(e) => {
                                            setSearchProductTerm(e.target.value);
                                            setShowProductSearch(true);
                                        }}
                                        onFocus={() => setShowProductSearch(true)}
                                        className={styles.input}
                                        placeholder="Digite o código ou nome do produto..."
                                        disabled={formLoading}
                                    />
                                    {searchProductTerm && showProductSearch && availableMaterials.length > 0 && (
                                        <div
                                            style={{
                                                position: 'fixed',
                                                width: '500px',
                                                maxWidth: 'calc(100% - 40px)',
                                                background: 'white',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: 'var(--border-radius-md)',
                                                maxHeight: '250px',
                                                overflowY: 'auto',
                                                zIndex: 9999,
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                                                left: 'auto',
                                                top: 'auto'
                                            }}
                                            ref={(el) => {
                                                if (el && searchProductTerm && showProductSearch) {
                                                    const inputEl = el.previousElementSibling;
                                                    if (inputEl) {
                                                        const rect = inputEl.getBoundingClientRect();
                                                        el.style.position = 'fixed';
                                                        el.style.top = `${rect.bottom + window.scrollY}px`;
                                                        el.style.left = `${rect.left + window.scrollX}px`;
                                                        el.style.width = `${rect.width}px`;
                                                    }
                                                }
                                            }}
                                        >
                                            {availableMaterials.map(material => (
                                                <div
                                                    key={material.id}
                                                    onClick={() => {
                                                        setSelectedMaterialId(material.id);
                                                        setSearchProductTerm(`${material.code} - ${material.commercialName}`);
                                                        setShowProductSearch(false);
                                                    }}
                                                    style={{
                                                        padding: '10px 12px',
                                                        cursor: 'pointer',
                                                        borderBottom: '1px solid var(--border-color)',
                                                        transition: 'background 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--header-bg)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                                >
                                                    <div style={{ fontWeight: 600 }}>{material.code}</div>
                                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                                        {material.commercialName}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {formErrors.material && (
                                    <small className={styles.errorHint}><FiAlertCircle size={12} style={{ marginRight: '4px' }} />{formErrors.material}</small>
                                )}
                                {selectedMaterial && (
                                    <div className={styles.infoBox} style={{ marginTop: '12px' }}>
                                        <FiCheck size={18} style={{ color: '#28a745' }} />
                                        <div>
                                            <strong>{selectedMaterial.code}</strong> - {selectedMaterial.commercialName}
                                            {selectedMaterial.inciName && <span style={{ color: 'var(--text-secondary)' }}> ({selectedMaterial.inciName})</span>}
                                        </div>
                                    </div>
                                )}
                                {materialsLoading && (
                                    <small className={styles.fieldHint}>Carregando matérias-primas...</small>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Fornecedor - Now shows based on selected product */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><MdBusiness size={18} /></div>
                            <h3 className={styles.sectionTitle}>Fornecedor</h3>
                            <span className={styles.sectionBadge}>Obrigatório</span>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div className={styles.inputGroup}>
                                <label>Fornecedor</label>
                                {selectedMaterial ? (
                                    availableSuppliersForMaterial.length > 0 ? (
                                        <select
                                            value={selectedSupplierId}
                                            onChange={(e) => {
                                                const supplierId = e.target.value;
                                                const supplier = availableSuppliersForMaterial.find(s => s.id === supplierId);
                                                setSelectedSupplierId(supplierId);
                                                setSelectedSupplierName(supplier?.name || '');
                                            }}
                                            className={styles.select}
                                            disabled={formLoading || suppliersLoading}
                                        >
                                            <option value="">Selecione um fornecedor...</option>
                                            {supplierOptionsForMaterial.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className={styles.errorHint} style={{ padding: '10px', background: '#fff3cd', borderRadius: '8px' }}>
                                            <FiAlertCircle size={16} style={{ marginRight: '8px' }} />
                                            Este produto não possui fornecedores cadastrados.
                                            Por favor, cadastre um fornecedor para este produto antes de continuar.
                                        </div>
                                    )
                                ) : (
                                    <div className={styles.infoBox} style={{ backgroundColor: '#e3f2fd' }}>
                                        <FiInfo size={18} />
                                        <span>Selecione um produto primeiro para ver os fornecedores disponíveis</span>
                                    </div>
                                )}
                                {formErrors.supplier && (
                                    <small className={styles.errorHint}><FiAlertCircle size={12} style={{ marginRight: '4px' }} />{formErrors.supplier}</small>
                                )}
                                {suppliersLoading && <small className={styles.fieldHint}>Carregando fornecedores...</small>}
                            </div>
                        </div>
                    </div>

                    {/* Informações da Movimentação */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiTrendingUp size={18} /></div>
                            <h3 className={styles.sectionTitle}>Informações da Movimentação</h3>
                        </div>
                        <div className={styles.formGrid}>
                            <div className={styles.inputGroup}>
                                <label>Inventário</label>
                                <select
                                    value={inventory}
                                    onChange={(e) => setInventory(e.target.value)}
                                    className={styles.select}
                                    disabled={formLoading}
                                >
                                    <option value="B2E">B2E - Disponível</option>
                                    <option value="B2E_BLOQUEADO">B2E - Bloqueado</option>
                                    <option value="TERCEIROS">Terceiros</option>
                                </select>
                                <small className={styles.fieldHint}>
                                    Para inventários de terceiros é necessário informar no Cadastro do Inventário se pertence a Cliente ou Fornecedor!
                                </small>
                            </div>

                            <div className={styles.inputGroup}>
                                <label>Quantidade <span className={styles.required}>*</span></label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    className={styles.input}
                                    placeholder="0,00"
                                    disabled={formLoading}
                                />
                                {formErrors.quantity && (
                                    <small className={styles.errorHint}><FiAlertCircle size={12} style={{ marginRight: '4px' }} />{formErrors.quantity}</small>
                                )}
                            </div>

                            <div className={styles.inputGroup}>
                                <label>Custo Estoque Unitário</label>
                                <input
                                    type="number"
                                    step="0.0001"
                                    value={unitCost}
                                    onChange={(e) => setUnitCost(e.target.value)}
                                    className={styles.input}
                                    placeholder="0,0000"
                                    disabled={formLoading}
                                />
                                <small className={styles.fieldHint}>Preço por unidade do produto</small>
                            </div>

                            {movementType === 'entrada' && (
                                <div className={styles.inputGroup}>
                                    <label>Nota Fiscal <span className={styles.required}>*</span></label>
                                    <input
                                        type="text"
                                        value={invoiceNumber}
                                        onChange={(e) => setInvoiceNumber(e.target.value)}
                                        className={styles.input}
                                        placeholder="Número da NF-e"
                                        disabled={formLoading}
                                    />
                                    {formErrors.invoiceNumber && (
                                        <small className={styles.errorHint}><FiAlertCircle size={12} style={{ marginRight: '4px' }} />{formErrors.invoiceNumber}</small>
                                    )}
                                </div>
                            )}

                            <div className={styles.inputGroup}>
                                <label>Data de Fabricação</label>
                                <input
                                    type="date"
                                    value={formatDateForInput(manufacturingDate)}
                                    onChange={(e) => setManufacturingDate(e.target.value)}
                                    className={styles.input}
                                    disabled={formLoading}
                                />
                            </div>

                            <div className={styles.inputGroup}>
                                <label>Data de Validade</label>
                                <input
                                    type="date"
                                    value={formatDateForInput(expiryDate)}
                                    onChange={(e) => setExpiryDate(e.target.value)}
                                    className={styles.input}
                                    disabled={formLoading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Observações */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiClipboard size={18} /></div>
                            <h3 className={styles.sectionTitle}>Observações</h3>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <textarea
                                value={receiptObservations}
                                onChange={(e) => setReceiptObservations(e.target.value)}
                                className={styles.textarea}
                                rows={3}
                                placeholder="Observações sobre o recebimento/movimentação..."
                                disabled={formLoading}
                            />
                        </div>
                    </div>

                    {/* Responsável */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiUser size={18} /></div>
                            <h3 className={styles.sectionTitle}>Responsável</h3>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div className={styles.inputGroup}>
                                <input
                                    type="text"
                                    value={currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário Logado'}
                                    className={styles.input}
                                    disabled
                                    style={{ backgroundColor: 'var(--header-bg)' }}
                                />
                                <small className={styles.fieldHint}>Usuário que está realizando a movimentação</small>
                            </div>
                        </div>
                    </div>

                    {/* Valor Total Preview */}
                    {quantity && unitCost && parseFloat(quantity) > 0 && parseFloat(unitCost) > 0 && (
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiDollarSign size={18} /></div>
                                <h3 className={styles.sectionTitle}>Resumo</h3>
                            </div>
                            <div className={styles.resumoGrid}>
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>Quantidade:</span>
                                    <span className={styles.resumoValor}>{parseFloat(quantity).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className={styles.resumoLinha}>
                                    <span className={styles.resumoLabel}>Custo Unitário:</span>
                                    <span className={styles.resumoValor}>R$ {parseFloat(unitCost).toLocaleString('pt-BR', { minimumFractionDigits: 4 })}</span>
                                </div>
                                <div className={`${styles.resumoLinha} ${styles.resumoTotal}`}>
                                    <span className={styles.resumoLabel}><strong>Valor Total:</strong></span>
                                    <span className={styles.resumoValorTotal}>
                                        R$ {(parseFloat(quantity) * parseFloat(unitCost)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.formActions}>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.secondaryButton}
                        disabled={formLoading}
                    >
                        <FiX size={18} /> Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        className={styles.primaryButton}
                        disabled={formLoading}
                    >
                        {formLoading ? (
                            <>
                                <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                Processando...
                            </>
                        ) : (
                            movementType === 'entrada' ? <><FiCheck size={18} /> Confirmar Entrada</> : <><FiCheck size={18} /> Confirmar Saída</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// 3. NFe SEARCH MODAL (FIXED - Added proper visibility and higher z-index)
// ============================================
const NFeSearchModal = ({ supplierName, supplierCnpj, onSelect, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedNFe, setSelectedNFe] = useState(null);

    // Mock NFe data - In real implementation, this would call an API
    const mockNFeList = useMemo(() => {
        return [
            {
                id: '1',
                numero: '123456',
                serie: '1',
                chaveAcesso: '35200312345678901234567890123456789012345678',
                dataEmissao: '15/12/2024',
                valorTotal: 15750.00,
                fornecedorNome: supplierName,
                fornecedorCnpj: supplierCnpj,
                status: 'autorizada'
            },
            {
                id: '2',
                numero: '123457',
                serie: '1',
                chaveAcesso: '35200312345678901234567890123456789012345679',
                dataEmissao: '10/12/2024',
                valorTotal: 8900.00,
                fornecedorNome: supplierName,
                fornecedorCnpj: supplierCnpj,
                status: 'autorizada'
            },
            {
                id: '3',
                numero: '123458',
                serie: '2',
                chaveAcesso: '35200312345678901234567890123456789012345680',
                dataEmissao: '05/12/2024',
                valorTotal: 3200.50,
                fornecedorNome: supplierName,
                fornecedorCnpj: supplierCnpj,
                status: 'cancelada'
            }
        ];
    }, [supplierName, supplierCnpj]);

    const handleSearch = async () => {
        setLoading(true);
        setTimeout(() => {
            let results = [...mockNFeList];
            if (searchTerm) {
                results = results.filter(nfe =>
                    nfe.numero.includes(searchTerm) ||
                    nfe.chaveAcesso.includes(searchTerm)
                );
            }
            setSearchResults(results);
            setLoading(false);
        }, 800);
    };

    const handleSelectNFe = (nfe) => {
        setSelectedNFe(nfe);
    };

    const handleConfirm = () => {
        if (selectedNFe) {
            onSelect(selectedNFe);
            onClose();
        }
    };

    return (
        <div
            className={styles.modalBackdrop}
            onClick={onClose}
            style={{ zIndex: 2000, backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
        >
            <div
                className={`${styles.formContainer} ${styles.formContainerLarge}`}
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh',
                    overflow: 'hidden'
                }}
            >
                <div className={styles.modalHeader}>
                    <div>
                        <h2><FiSearch size={24} style={{ marginRight: '10px' }} /> Buscar NF-e do Fornecedor</h2>
                        <p className={styles.modalSubtitle}>
                            Fornecedor: <strong>{supplierName}</strong>
                            {supplierCnpj && ` | CNPJ: ${supplierCnpj}`}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.modalCloseButton}
                    >
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.formContent}>
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiSearch size={18} /></div>
                            <h3 className={styles.sectionTitle}>Buscar Nota Fiscal</h3>
                        </div>
                        <div className={styles.formGrid}>
                            <div className={styles.inputGroup}>
                                <label>Número da NF-e ou Chave de Acesso</label>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className={styles.input}
                                        placeholder="Digite o número ou chave de acesso..."
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleSearch}
                                        className={styles.primaryButton}
                                        disabled={loading}
                                    >
                                        {loading ? <><FiRefreshCw size={18} className={styles.buttonSpinner} /> Buscando...</> : <><FiSearch size={18} /> Buscar</>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {searchResults.length > 0 && (
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiFileText size={18} /></div>
                                <h3 className={styles.sectionTitle}>NF-e Encontradas</h3>
                                <span className={styles.sectionBadge}>{searchResults.length} notas</span>
                            </div>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Selecionar</th>
                                            <th>Número</th>
                                            <th>Série</th>
                                            <th>Data Emissão</th>
                                            <th>Valor Total</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {searchResults.map(nfe => (
                                            <tr key={nfe.id} style={{ backgroundColor: selectedNFe?.id === nfe.id ? 'var(--primary-light)' : 'transparent' }}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="radio"
                                                        name="selectedNFe"
                                                        checked={selectedNFe?.id === nfe.id}
                                                        onChange={() => handleSelectNFe(nfe)}
                                                    />
                                                </td>
                                                <td><strong>{nfe.numero}</strong></td>
                                                <td>{nfe.serie}</td>
                                                <td>{nfe.dataEmissao}</td>
                                                <td className={styles.priceCell}>
                                                    R$ {nfe.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                </td>
                                                <td>
                                                    <span className={`${styles.statusBadge} ${nfe.status === 'autorizada' ? styles.statusBadgeSuccess : styles.statusBadgeDanger}`}>
                                                        {nfe.status === 'autorizada' ? 'Autorizada' : 'Cancelada'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {!loading && searchTerm && searchResults.length === 0 && (
                        <div className={styles.infoBox} style={{ backgroundColor: '#fff3cd', borderLeftColor: '#ffc107' }}>
                            <FiAlertCircle size={20} />
                            <span>Nenhuma NF-e encontrada para os critérios informados.</span>
                        </div>
                    )}
                </div>

                <div className={styles.formActions}>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.secondaryButton}
                    >
                        <FiX size={18} /> Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        className={styles.primaryButton}
                        disabled={!selectedNFe}
                    >
                        <FiCheck size={18} /> Confirmar Seleção
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// 4. RECEIPT MODAL (Fixed NFe modal rendering)
// ============================================
const ReceiptModal = ({ order, onClose, onConfirm, onSearchNFe }) => {
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [selectedNFe, setSelectedNFe] = useState(null);
    const [receiptItems, setReceiptItems] = useState([]);
    const [receiptDate, setReceiptDate] = useState('');
    const [receiptNotes, setReceiptNotes] = useState('');
    const [isPartialReceipt, setIsPartialReceipt] = useState(false);
    const [showNFeModal, setShowNFeModal] = useState(false);

    useEffect(() => {
        const today = new Date().toLocaleDateString('pt-BR');
        setReceiptDate(today);

        if (order?.items) {
            setReceiptItems(order.items.map(item => ({
                ...item,
                receivedQuantity: item.quantity || 0,
                isDamaged: false,
                damagedQuantity: 0,
                notes: ''
            })));
        }
    }, [order]);

    if (!order) return null;

    const handleQuantityChange = (itemId, value) => {
        setReceiptItems(prev => prev.map(item => {
            if (item.id === itemId) {
                const newQuantity = Math.min(parseFloat(value) || 0, item.quantity);
                return { ...item, receivedQuantity: newQuantity };
            }
            return item;
        }));
    };

    const handleDamagedChange = (itemId, checked, value) => {
        setReceiptItems(prev => prev.map(item => {
            if (item.id === itemId) {
                if (checked) {
                    const damagedQty = Math.min(parseFloat(value) || 0, item.receivedQuantity);
                    return { ...item, isDamaged: true, damagedQuantity: damagedQty };
                } else {
                    return { ...item, isDamaged: false, damagedQuantity: 0 };
                }
            }
            return item;
        }));
    };

    const handleNFeSelected = (nfe) => {
        setSelectedNFe(nfe);
        setInvoiceNumber(nfe.numero);
        setShowNFeModal(false);
    };

    const handleConfirm = () => {
        if (!invoiceNumber && !selectedNFe) {
            alert('Por favor, informe o número da Nota Fiscal ou busque uma NF-e.');
            return;
        }

        const receiptData = {
            orderId: order.id,
            orderCode: order.code,
            supplierName: order.supplierName,
            invoiceNumber: invoiceNumber || selectedNFe?.numero,
            nfeData: selectedNFe,
            receiptDate,
            receiptNotes,
            isPartialReceipt,
            items: receiptItems.map(item => ({
                id: item.id,
                materialId: item.materialId,
                code: item.code,
                name: item.name,
                orderedQuantity: item.quantity,
                receivedQuantity: item.receivedQuantity,
                isDamaged: item.isDamaged,
                damagedQuantity: item.damagedQuantity,
                goodQuantity: item.receivedQuantity - (item.isDamaged ? item.damagedQuantity : 0),
                notes: item.notes
            })),
            totalReceived: receiptItems.reduce((sum, item) => sum + item.receivedQuantity, 0),
            totalDamaged: receiptItems.reduce((sum, item) => sum + (item.isDamaged ? item.damagedQuantity : 0), 0),
            totalGood: receiptItems.reduce((sum, item) => sum + (item.receivedQuantity - (item.isDamaged ? item.damagedQuantity : 0)), 0),
            createdAt: new Date(),
            createdBy: 'Usuário'
        };

        onConfirm(receiptData);
        onClose();
    };

    const totalOrderItems = order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
    const totalReceived = receiptItems.reduce((sum, item) => sum + item.receivedQuantity, 0);
    const receiptPercentage = totalOrderItems > 0 ? (totalReceived / totalOrderItems) * 100 : 0;

    return (
        <>
            {/* Render NFeSearchModal OUTSIDE the main modal container to avoid nesting issues */}
            {showNFeModal && (
                <NFeSearchModal
                    supplierName={order.supplierName}
                    supplierCnpj={order.supplierCnpj}
                    onSelect={handleNFeSelected}
                    onClose={() => setShowNFeModal(false)}
                />
            )}

            <div className={styles.modalBackdrop} onClick={onClose}>
                <div className={`${styles.formContainer} ${styles.formContainerXLarge}`} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h2><FiPackage size={24} style={{ marginRight: '10px' }} /> Efetuar Recebimento</h2>
                            <p className={styles.modalSubtitle}>
                                Pedido: <strong>{order.code}</strong> - {order.supplierName}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className={styles.modalCloseButton}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    <div className={styles.formContent}>
                        {/* Bloco da Nota Fiscal */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiFileText size={18} /></div>
                                <h3 className={styles.sectionTitle}>Dados da Nota Fiscal</h3>
                                <span className={styles.sectionBadge}>Obrigatório</span>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>Nº da Nota Fiscal</label>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <input
                                            type="text"
                                            value={invoiceNumber}
                                            onChange={(e) => setInvoiceNumber(e.target.value)}
                                            className={styles.input}
                                            placeholder="Número da NF-e"
                                            style={{ flex: 1 }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowNFeModal(true);
                                            }}
                                            className={styles.secondaryButton}
                                        >
                                            <FiSearch size={18} /> Buscar e validar NF-e
                                        </button>
                                    </div>
                                    {selectedNFe && (
                                        <small className={styles.successHint}>
                                            <FiCheck size={12} style={{ marginRight: '4px' }} />
                                            NF-e selecionada: {selectedNFe.numero} | Série: {selectedNFe.serie} |
                                            Data: {selectedNFe.dataEmissao} | Valor: R$ {selectedNFe.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </small>
                                    )}
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>Data do Recebimento</label>
                                    <input
                                        type="text"
                                        value={receiptDate}
                                        onChange={(e) => setReceiptDate(e.target.value)}
                                        className={styles.input}
                                        placeholder="dd/mm/aaaa"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Informações Básicas do Pedido */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiClipboard size={18} /></div>
                                <h3 className={styles.sectionTitle}>Informações do Pedido</h3>
                            </div>
                            <div className={styles.infoGrid}>
                                <div className={styles.infoItem}>
                                    <label>Código</label>
                                    <span>{order.code}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <label>Data do Pedido</label>
                                    <span>{order.orderDate}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <label>Status</label>
                                    <StatusBadge status={order.status} />
                                </div>
                                <div className={styles.infoItem}>
                                    <label>Responsável</label>
                                    <span>{order.responsible}</span>
                                </div>
                            </div>
                        </div>

                        {/* Approval Information */}
                        {(order.approvedBy || order.approvalNotes) && (
                            <div className={styles.formSection}>
                                <div className={styles.sectionHeader}>
                                    <div className={styles.sectionIcon}><FiCheck size={18} /></div>
                                    <h3 className={styles.sectionTitle}>Informações de Aprovação</h3>
                                </div>
                                <div className={styles.infoGrid}>
                                    {order.approvedBy && (
                                        <div className={styles.infoItem}>
                                            <label>Aprovado por</label>
                                            <span>{order.approvedBy}</span>
                                        </div>
                                    )}
                                    {order.approvedAt && (
                                        <div className={styles.infoItem}>
                                            <label>Data da Aprovação</label>
                                            <span>
                                                {typeof order.approvedAt === 'object'
                                                    ? order.approvedAt.toLocaleDateString?.('pt-BR')
                                                    : order.approvedAt}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                {order.approvalNotes && (
                                    <div className={styles.approvalNotes}>
                                        <strong>Observações da Aprovação</strong>
                                        <p>{order.approvalNotes}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Fornecedor */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><MdBusiness size={18} /></div>
                                <h3 className={styles.sectionTitle}>Fornecedor</h3>
                            </div>
                            <div className={styles.infoGrid}>
                                <div className={styles.infoItem}>
                                    <label>Nome</label>
                                    <span>{order.supplierName}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <label>Endereço</label>
                                    <span>{order.supplierAddress || 'Não informado'}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <label>Conta Contábil</label>
                                    <span>{order.supplierAccount || 'Não informada'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Itens do Pedido com Controle de Recebimento */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                                <h3 className={styles.sectionTitle}>Itens do Pedido - Controle de Recebimento</h3>
                                <span className={styles.sectionBadge}>
                                    Recebido: {totalReceived}/{totalOrderItems} ({receiptPercentage.toFixed(0)}%)
                                </span>
                            </div>
                            <div className={styles.tableContainer}>
                                <table className={styles.itemTable}>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Código</th>
                                            <th>Nome do Produto</th>
                                            <th>Qtde. Pedida</th>
                                            <th>Qtde. Recebida</th>
                                            <th>Avariado?</th>
                                            <th>Qtde. Avariada</th>
                                            <th>Obs.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {order.items && order.items.length > 0 ? (
                                            order.items.map((item, index) => {
                                                const receiptItem = receiptItems.find(ri => ri.id === item.id);
                                                const maxQuantity = item.quantity || 0;

                                                return (
                                                    <tr key={item.id}>
                                                        <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                                        <td>{item.code}</td>
                                                        <td>{item.name}</td>
                                                        <td className="text-right">{item.quantity}</td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={receiptItem?.receivedQuantity || 0}
                                                                onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                                className={styles.input}
                                                                style={{ width: '100px' }}
                                                                min="0"
                                                                max={maxQuantity}
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={receiptItem?.isDamaged || false}
                                                                onChange={(e) => handleDamagedChange(item.id, e.target.checked, receiptItem?.damagedQuantity || 0)}
                                                                style={{ width: '20px', height: '20px' }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={receiptItem?.damagedQuantity || 0}
                                                                onChange={(e) => handleDamagedChange(item.id, true, e.target.value)}
                                                                className={styles.input}
                                                                style={{ width: '100px' }}
                                                                disabled={!receiptItem?.isDamaged}
                                                                min="0"
                                                                max={receiptItem?.receivedQuantity || 0}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                value={receiptItem?.notes || ''}
                                                                onChange={(e) => {
                                                                    setReceiptItems(prev => prev.map(ri =>
                                                                        ri.id === item.id ? { ...ri, notes: e.target.value } : ri
                                                                    ));
                                                                }}
                                                                className={styles.input}
                                                                style={{ width: '150px' }}
                                                                placeholder="Observações"
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className={styles.emptyTable}>Nenhum item encontrado</td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr className={styles.tableFooterRow}>
                                            <td colSpan="3" className="text-right"><strong>Totais:</strong></td>
                                            <td className="text-right"><strong>{totalOrderItems}</strong></td>
                                            <td className="text-right"><strong>{totalReceived}</strong></td>
                                            <td colSpan="3"></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        {/* Entrega */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiTruck size={18} /></div>
                                <h3 className={styles.sectionTitle}>Entrega</h3>
                            </div>
                            <div className={styles.infoGrid}>
                                <div className={styles.infoItem}>
                                    <label>Tipo de Entrega</label>
                                    <span>{DELIVERY_TYPES.find(d => d.value === order.deliveryType)?.label || order.deliveryType}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <label>Frete por conta</label>
                                    <span>{FREIGHT_BY_OPTIONS.find(f => f.value === order.freightBy)?.label || order.freightBy}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <label>Transportadora</label>
                                    <span>{order.selectedCarrierName || 'Não informada'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Observações do Recebimento */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiClipboard size={18} /></div>
                                <h3 className={styles.sectionTitle}>Observações do Recebimento</h3>
                            </div>
                            <div style={{ padding: '20px' }}>
                                <textarea
                                    value={receiptNotes}
                                    onChange={(e) => setReceiptNotes(e.target.value)}
                                    className={styles.textarea}
                                    rows={3}
                                    placeholder="Observações adicionais sobre o recebimento..."
                                />
                            </div>
                        </div>

                        {/* Opção de Recebimento Parcial */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiSettings size={18} /></div>
                                <h3 className={styles.sectionTitle}>Opções</h3>
                            </div>
                            <div style={{ padding: '20px' }}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={isPartialReceipt}
                                        onChange={(e) => setIsPartialReceipt(e.target.checked)}
                                    />
                                    <span>Este é um recebimento parcial (mais itens serão recebidos posteriormente)</span>
                                </label>
                                {isPartialReceipt && (
                                    <small className={styles.fieldHint} style={{ display: 'block', marginTop: '8px' }}>
                                        O pedido permanecerá com status "Recebimento Parcial" até que todos os itens sejam recebidos.
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.formActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            className={styles.secondaryButton}
                        >
                            <FiX size={18} /> Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirm}
                            className={styles.primaryButton}
                        >
                            <FiCheck size={18} /> Confirmar Recebimento
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

// ============================================
// 5. MAIN COMPONENT
// ============================================
const PurchaseOrderEntryPage = () => {
    const can = usePagePermissions('entrada_do_pedido_de_compra');

    // States
    const [receiptOrder, setReceiptOrder] = useState(null);
    const [trackingOrder, setTrackingOrder] = useState(null);
    const [showManualEntryModal, setShowManualEntryModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [toasts, setToasts] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);

    // Hooks
    const { currentUser, loading: authLoading } = useAuth();
    const {
        data: orders,
        loading: ordersLoading,
        updateDocument,
        refreshData
    } = useFirestore(PURCHASE_ORDERS_COLLECTION, 'createdAt', true);

    // Separate hooks for other collections
    const { addDocument: addReceivingRecord } = useFirestore(RECEIVING_RECORDS_COLLECTION);
    const { addDocument: addStockMovement } = useFirestore(STOCK_MOVEMENTS_COLLECTION);

    // Toast functions - using global Toast component
    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    // Handle manual stock entry confirmation
    const handleManualStockEntry = async (movementData) => {
        setActionLoading(true);

        try {
            const result = await addStockMovement(movementData);

            if (result.success) {
                addToast(
                    `${movementData.movementTypeLabel} registrada com sucesso!`,
                    'Success'
                );
                await refreshData();
            } else {
                addToast(`Erro ao registrar movimentação: ${result.error}`, 'Error');
            }
        } catch (error) {
            console.error('Stock movement error:', error);
            addToast('Ocorreu um erro inesperado.', 'Error');
        } finally {
            setActionLoading(false);
            setShowManualEntryModal(false);
        }
    };

    // Filter orders that are approved but pending entry (not fully received/completed)
    const pendingEntryOrders = useMemo(() => {
        if (!orders) return [];

        const lowerSearch = searchTerm.toLowerCase();

        return orders.filter(order => {
            const shouldShow = order.status === ORDER_STATUS.APPROVED ||
                order.status === ORDER_STATUS.PENDING_ENTRY ||
                order.status === ORDER_STATUS.PARTIALLY_RECEIVED;

            if (!shouldShow) return false;

            const matchesSearch = (order.code && order.code.toLowerCase().includes(lowerSearch)) ||
                (order.supplierName && order.supplierName.toLowerCase().includes(lowerSearch));

            return matchesSearch;
        }).sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return dateB - dateA;
        });
    }, [orders, searchTerm]);

    // Pagination
    const totalPages = Math.ceil(pendingEntryOrders.length / ITEMS_PER_PAGE);
    const currentOrders = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return pendingEntryOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [pendingEntryOrders, currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    // Handle receipt confirmation
    const handleReceiptConfirm = async (receiptData) => {
        setActionLoading(true);

        try {
            const totalOrdered = receiptData.items.reduce((sum, item) => sum + item.orderedQuantity, 0);
            const totalReceived = receiptData.items.reduce((sum, item) => sum + item.receivedQuantity, 0);
            const isFullyReceived = totalReceived >= totalOrdered;

            let newStatus;
            if (isFullyReceived) {
                newStatus = ORDER_STATUS.FULLY_RECEIVED;
            } else if (receiptData.isPartialReceipt) {
                newStatus = ORDER_STATUS.PARTIALLY_RECEIVED;
            } else {
                newStatus = ORDER_STATUS.PENDING_ENTRY;
            }

            const updateData = {
                status: newStatus,
                receiptDate: receiptData.receiptDate,
                receiptNotes: receiptData.receiptNotes,
                invoiceNumber: receiptData.invoiceNumber,
                nfeData: receiptData.nfeData,
                receiptItems: receiptData.items,
                totalReceived: receiptData.totalReceived,
                totalDamaged: receiptData.totalDamaged,
                totalGood: receiptData.totalGood,
                updatedAt: new Date()
            };

            const result = await updateDocument(receiptData.orderId, updateData);

            if (result.success) {
                await addReceivingRecord({
                    ...receiptData,
                    orderStatus: newStatus,
                    receivedBy: currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário'
                });

                addToast(`Recebimento do pedido ${receiptData.orderCode} registrado com sucesso!`, 'Success');
                await refreshData();
            } else {
                addToast(`Erro ao registrar recebimento: ${result.error}`, 'Error');
            }
        } catch (error) {
            console.error('Receipt error:', error);
            addToast('Ocorreu um erro inesperado.', 'Error');
        } finally {
            setActionLoading(false);
            setReceiptOrder(null);
        }
    };

    // Get status class for styling
    const getStatusClass = (status) => {
        const statusConfig = ORDER_STATUS_LABELS[status] || ORDER_STATUS_LABELS[ORDER_STATUS.PENDING];
        return statusConfig.className;
    };

    const getStatusLabel = (status) => {
        const statusConfig = ORDER_STATUS_LABELS[status] || ORDER_STATUS_LABELS[ORDER_STATUS.PENDING];
        return statusConfig.label;
    };

    // Show skeleton while loading
    const showSkeleton = useMemo(() => {
        if (ordersLoading && (!orders || orders.length === 0)) return true;
        return false;
    }, [ordersLoading, orders]);

    return (
        <div className={styles.pageContainer}>
            {/* Toast Container - Using global Toast component */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Modals */}
            {receiptOrder && (
                <ReceiptModal
                    order={receiptOrder}
                    onClose={() => setReceiptOrder(null)}
                    onConfirm={handleReceiptConfirm}
                />
            )}

            {trackingOrder && (
                <TrackingModal
                    order={trackingOrder}
                    onClose={() => setTrackingOrder(null)}
                    onUpdateStatus={async (orderId, status, description) => {
                        addToast(`Status atualizado para ${status}`, 'Success');
                    }}
                />
            )}

            {showManualEntryModal && (
                <ManualStockEntryModal
                    onClose={() => setShowManualEntryModal(false)}
                    onConfirm={handleManualStockEntry}
                />
            )}

            {/* Header - Using global PageHeader */}
            <PageHeader
                title="Entrada de Pedidos"
                actionText={can.create ? "Movimentação Manual" : ""}
                onAction={() => setShowManualEntryModal(true)}
                disabled={actionLoading || !can.create}
                showAction={can.create}
            />

            {/* Search Bar - Using global SearchBar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Código do pedido, fornecedor..."
                    onSearch={(value) => {
                        setSearchTerm(value);
                        setCurrentPage(1);
                    }}
                    initialValue={searchTerm}
                    debounceDelay={300}
                    disabled={actionLoading}
                />
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Código</th>
                            <th>Previsão de Entrega</th>
                            <th>Fornecedor</th>
                            <th>Status</th>
                            <th style={{ width: '280px' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {showSkeleton ? (
                            <tr>
                                <td colSpan="5">
                                    <TableSkeleton columns={5} rows={ITEMS_PER_PAGE} />
                                </td>
                            </tr>
                        ) : currentOrders && currentOrders.length > 0 ? (
                            currentOrders.map(order => (
                                <tr key={order.id} className={styles.tableRow}>
                                    <td><strong>{order.code}</strong></td>
                                    <td>
                                        {order.estimatedDelivery || (
                                            <span style={{ color: 'var(--text-muted)' }}>Não informada</span>
                                        )}
                                    </td>
                                    <td>{order.supplierName}</td>
                                    <td>
                                        <StatusBadge status={order.status} />
                                    </td>
                                    <td>
                                        <div className={styles.actionsContainer}>
                                            {can.approve && (
                                                <button
                                                    onClick={() => setReceiptOrder(order)}
                                                    className={`${styles.actionButton} ${styles.receiptButton}`}
                                                    disabled={actionLoading}
                                                    title="Efetuar Recebimento"
                                                >
                                                    <FiPackage size={14} />
                                                </button>
                                            )}
                                            {can.track && (
                                                <button
                                                    onClick={() => setTrackingOrder(order)}
                                                    className={`${styles.actionButton} ${styles.trackButton}`}
                                                    disabled={actionLoading}
                                                    title="Rastrear"
                                                >
                                                    <FiMapPin size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5">
                                    <EmptyState
                                        icon="📦"
                                        title={searchTerm ? 'Nenhum pedido encontrado com o termo buscado.' : 'Nenhum pedido aguardando entrada no momento.'}
                                        message={searchTerm ? 'Tente buscar por outro termo.' : 'Pedidos aprovados aparecerão aqui para recebimento.'}
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination - Using global Pagination */}
            {!showSkeleton && totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={pendingEntryOrders.length}
                    onPageChange={handlePageChange}
                    disabled={actionLoading}
                    itemsLabel="pedidos"
                />
            )}
        </div>
    );
};

export default PurchaseOrderEntryPage;